// src/pages/DataQuality/components/PlotTypeSelector.js
import React from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import { motion } from "framer-motion";
import PropTypes from "prop-types";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { Tooltip } from "react-tooltip";

const PlotTypeSelector = ({
  plotTypes,
  selectedPlotType,
  onSelectPlotType,
  className = "",
}) => {
  // EIRNA primary green color
  const EIRNA_GREEN = "#2F9E37";

  // column layout: number of items per column
  const layout=[3,3,2,3,3]
  const cols=[];
  let s=0;
  layout.forEach(n=>{cols.push(plotTypes.slice(s,s+=n));});
  if (s<plotTypes.length) {
    cols.push(plotTypes.slice(s));
  }


  // InfoTooltip component for plot type descriptions
  const InfoTooltip = ({ id, content, placement = "top" }) => (
    <>
      <AiOutlineInfoCircle
        title={content}
        style={{
          marginLeft: "4px",
          cursor: "help",
          color: "#6B7280",
          fontSize: "12px",
          verticalAlign: "middle",
          display: "inline-flex",
          alignItems: "center",
        }}
      />
    </>
  );

  // Tooltip content for each plot type based on HTML descriptions
  const getTooltipContent = (plotTypeId) => {
    const tooltips = {
      trip_periodicity:
        "This plot shows the triplet periodicity across a given range of readlengths. The user may decide the minimum and maximum readlengths in the settings for this plot. Each readlength will have three bars associated with it corresponding to one of three frames the reads align to. A particular read length is considered to have strong triplet periodicity if the count in one of the frames is much higher than the other two frames. The legend in the top right will also show a score based on the periodicity of most frequent read length. A score is calculated for most frequent read length using the formula: 1-(count of the second highest peak/count of the highest peak). This will result in readlengths with strong periodicity receiving a score close to 1, while readlengths with weak periodicity receive a score close to 0. The better the triplet periodicity for a given study the clearer it will be which frame is being translated when viewing reads from this dataset on the subcodon profile plot.",

      metagene_plot:
        "This plot shows counts on the y-axis and a position relative to some sequence feature on the x-axis, this can be the annotated start or stop codon. All reads from any gene that lie near the chosen position will be plotted, this can be based off the 5' end of reads, or the inferred A-site. A maximum of 6 files can be selected if not aggregating.",

      custom_metagene:
        "This plot is similar to the metagene plot, but allows you to build a metagene around a custom list of sequences on the fly. Simply input a sequence or comma separated list of sequences into the 'Custom sequence list' input box and then optionally limit where to search the transcripts for that sequence.",

      nuc_comp:
        "This plot shows the nucleotide composition of all selected reads. This can be either shown as percentages (per) or raw counts (count). If 'per' is selected the plot will show the percentage of each nucleotide across all reads. If 'count' is selected the plot will show the raw counts of each nucleotide across all reads. The reads used for this analysis can be either all mapped reads or only unmapped reads. If 'mapped' is selected only reads that have been successfully aligned to the transcriptome will be used for the analysis. If 'unmapped' is selected only reads that have not been successfully aligned to the transcriptome will be used for the analysis.",

      readlen_dist:
        "This plot shows the read length distribution of all selected files. The x-axis shows the read length and the y-axis shows the count of reads for each length. This plot can be used to assess the quality of the data and to identify any issues with the sequencing. If 'ambig' is checked ambiguous reads (reads that map to multiple locations) will be included in the plot. If 'ambig' is unchecked only uniquely mapped reads will be included in the plot.",

      heatmap:
        "This plot shows a heatmap of read counts across different positions relative to the start or stop codon. The x-axis shows the position relative to the start/stop codon and the y-axis shows the read length. The color intensity represents the count of reads at each position and length combination. This plot can be used to identify patterns in read distribution and to assess the quality of the data.",

      mapped_reads_plot:
        "This plot shows a breakdown of reads into multiple categories for each file. The categories include mapped reads, unmapped reads, ambiguous reads, and other quality metrics. This plot can be used to assess the overall quality of the data and to identify any issues with the sequencing or alignment process.",

      mrna_dist:
        "This plot shows the proportion of reads aligning to different regions of the transcriptome. The regions include 5' UTR, CDS, 3' UTR, and other features. This plot can be used to assess the distribution of reads across different transcriptomic regions and to identify any biases in the data.",

      mrna_dist_readlen:
        "This plot shows the read length distribution across different RNA regions. The x-axis shows the read length and the y-axis shows the count of reads for each length, broken down by region. This plot can be used to assess how read length varies across different transcriptomic regions.",

      cluster:
        "This plot shows hierarchical clustering of samples based on their expression profiles. The clustering is performed using correlation-based distance metrics and can be used to identify groups of samples with similar expression patterns.",

      replicate_comp:
        "This plot shows correlation analysis between replicates. It maps counts for every transcript from two different files and shows the correlation coefficient. This plot can be used to assess the reproducibility of the data and to identify any systematic differences between replicates.",

      te: "This plot shows translation efficiency (TE) analysis. It compares Ribo-Seq and RNA-Seq data to identify genes that are being actively translated. The plot can show raw counts, RPKM, or TPM values for both Ribo-Seq and RNA-Seq data.",

      unmapped:
        "This plot shows the most frequent unmapped reads. It displays a sorted table of the top 100 most frequently unmapped reads, which can be used to identify potential contaminants, adapter sequences, or other issues with the data.",
      qc_summary:
        "This plot shows a summary of key data quality metrics, such as triplet periodicity, read length distribution, unapped reads etc. Each sample will be placed into either PASS or WARN categories, this can be used to quickly identify samples that may need closer inspection.",
    };

    return tooltips[plotTypeId] || "Plot description not available.";
  };

  return (
    <Card className={`border-0 shadow-sm ${className} `}>
      <CardHeader>
        <div className="d-flex justify-content-between align-items-center">
          <h5
            className="mb-0 fw-semibold d-flex align-items-center"
            style={{ color: "#1a6d51" }}
          >
            <i className="ri-bar-chart-line me-2 " />
            Choose Plot Type
          </h5>
        </div>
      </CardHeader>

      <CardBody>
        {/* Plot Type Pills */}
        <div className="d-flex gap-4">
          {cols.map((col,ci)=> (
            <div key={ci} className="d-flex flex-column gap-2">
            {col.map((plotType, index) => (
            <motion.button
              key={plotType.id}
              type="button"
              className="btn position-relative"
              onClick={() => onSelectPlotType(plotType.id)}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                delay: index * 0.03,
                type: "spring",
                stiffness: 200,
                damping: 20,
              }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              style={{
                backgroundColor:
                  plotType.id === selectedPlotType
                    ? EIRNA_GREEN
                    : "transparent",
                borderColor: EIRNA_GREEN,
                color: plotType.id === selectedPlotType ? "white" : EIRNA_GREEN,
                fontWeight: plotType.id === selectedPlotType ? "600" : "500",
                padding: "4px 10px",
                borderRadius: "18px",
                fontSize: "12px",
                border: `2px solid ${EIRNA_GREEN}`,
                cursor: "pointer",
                transition: "all 0.2s ease",
                whiteSpace: "normal",
                outline: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                minHeight: "28px",
                boxShadow:
                  plotType.id === selectedPlotType
                    ? `0 3px 8px ${EIRNA_GREEN}35`
                    : "none",
              }}
              onMouseEnter={(e) => {
                if (plotType.id !== selectedPlotType) {
                  e.target.style.backgroundColor = `${EIRNA_GREEN}10`;
                  e.target.style.transform = "translateY(-1px)";
                }
              }}
              onMouseLeave={(e) => {
                if (plotType.id !== selectedPlotType) {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.transform = "translateY(0)";
                }
              }}
              aria-pressed={plotType.id === selectedPlotType}
              aria-label={`Select ${plotType.label} plot type`}
            >
              {/* Icon */}
              <span style={{ fontSize: "13px" }}>{plotType.icon}</span>

              {/* Label Container */}
              <span
                style={{
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "2px",
                }}
              >
                {plotType.label}
                <InfoTooltip
                  id={`tt-${plotType.id}`}
                  content={getTooltipContent(plotType.id)}
                  placement="top"
                />
              </span>

              {/* Selection Indicator */}
              {plotType.id === selectedPlotType && (
                <motion.span
                  style={{
                    position: "absolute",
                    top: "-6px",
                    right: "-6px",
                    width: "16px",
                    height: "16px",
                    backgroundColor: "rgba(255,255,255,0.95)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    color: EIRNA_GREEN,
                    fontWeight: "bold",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    delay: 0.1,
                    type: "spring",
                    stiffness: 400,
                    damping: 25,
                  }}
                >
                  ✓
                </motion.span>
              )}
            </motion.button>
            ))}
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

PlotTypeSelector.propTypes = {
  plotTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      description: PropTypes.string,
    })
  ).isRequired,
  selectedPlotType: PropTypes.string,
  onSelectPlotType: PropTypes.func.isRequired,
  className: PropTypes.string,
};

export default PlotTypeSelector;